import { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import queryString from 'query-string';
import * as authAPI from '@/api/auth';
import { addFollowingList } from '@/services/mylist/Actions';
import { Button } from '@/components/ui/button';
import Checkbox from '@/ui/checkbox/defaultCheckbox/CheckBox';
import { saveUserId, saveInfo } from '@/services/user/Actions';
import FindPassword from '@/components/authenticationPage/findPassword';
import PreLoader from '@/ui/preloaders/screenPreloader/ScreenPreloader';
import { getUserData } from '@/api/user';
import { getFollowList } from '@/api/followings';
import Input from '@/ui/inputs/Input';
import Reverify from '../reVerify/Reverify';

const SigninContent = ({ onRegister }: { onRegister: () => void }) => {
  const [t] = useTranslation('signIn');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isChecked, setIsChecked] = useState(false);
  const [error, setError] = useState(false);
  const [openResetPwd, setOpenResetPwd] = useState(false);
  const [activatePreloader, setActivatePreloader] = useState(false);
  const [openReverify, setOpenReverify] = useState(false);

  const handleEnter = (e) => {
    if (e.keyCode === 13) {
      handleLogin();
    }
  };
  const redirectionUrl = queryString.parse(window.location.search);

  const { id } = useParams();
  const location = useLocation();

  useEffect(() => {
    const email = JSON.parse(localStorage.getItem('Email'));
    const isChecked = JSON.parse(localStorage.getItem('Checkbox'));

    if (email) setEmail(email);
    if (isChecked) setIsChecked(isChecked);
  }, []);

  const dispatch = useDispatch();

  const handleLogin = async () => {
    if (isChecked) {
      localStorage.setItem('Email', JSON.stringify(email));
      localStorage.setItem('Checkbox', JSON.stringify(isChecked));
    } else {
      localStorage.removeItem('Email');
      localStorage.setItem('Checkbox', JSON.stringify(isChecked));
    }

    setActivatePreloader(true);

    const result = await authAPI.login({
      email,
      password,
      expire_seconds: 41472000,
    });

    if (result && result.status === 200 && 'data' in result) {
      const data = result.data;
      localStorage.setItem('access_token', data.access_token);

      const userInfoRes = await getUserData(data.user_id);
      if (!userInfoRes) return;
      if (data.has_temp_password) {
        dispatch(saveUserId(data.user_id));
        dispatch(saveInfo(userInfoRes.data));
        if (redirectionUrl.redirection) {
          setTimeout(() => {
            window.location.href = redirectionUrl.redirection as string;
          }, 200);
        } else {
          setTimeout(() => {
            window.location.href = '/';
          }, 200);
        }
      } else {
        dispatch(saveUserId(data.user_id));
        dispatch(saveInfo(userInfoRes.data));
        if (redirectionUrl.redirection) {
          setTimeout(() => {
            window.location.href = redirectionUrl.redirection + `${data.user_id}` + `/${data.data}`;
          }, 200);
        } else {
          setTimeout(() => {
            if (id) {
              // check if it redirect to company detail
              const isRedirectToCompany = location.pathname
                .split('/')
                .find((item) => item === 'company');
              if (isRedirectToCompany) window.location.href = '/company/' + id;
              else window.location.href = '/' + id;
            } else window.location.href = '/';
          }, 200);
        }
      }

      const followList = await getFollowList();
      dispatch(addFollowingList(followList.data));
    } else if (result && result.status === 403) {
    } else {
      setError(true);
    }
    setActivatePreloader(false);
  };

  return (
    <>
      <PreLoader activate={activatePreloader} />

      <div className="flex flex-col gap-4">
        <div className="flex flex-col items-center gap-2 text-center">
          <h1 className="text-2xl font-bold">{t('Login to your account')}</h1>
          <p className="text-muted-foreground text-sm text-balance">
            {t('Enter your email below to login to your account')}
          </p>
        </div>
        <div className="space-y-2">
          <Input
            placeHolder="Email"
            value={email}
            onChange={(e: any) => setEmail(e.target.value)}
            className="h-11"
          />
          <Input
            placeHolder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => handleEnter(e)}
            type="password"
            helperText={error && t('Incorrect Email/Password')}
            className="h-11"
          />
        </div>

        <div className="flex items-center" onClick={(e) => setIsChecked(!isChecked)}>
          <Checkbox
            isChecked={isChecked}
            className="mx-2"
            onClick={(e) => setIsChecked(!isChecked)}
          />
          <div className="text-sm text-text-400">{t('Remember me')}</div>
        </div>
        <div className="space-y-2">
          <Button
            onClick={() => handleLogin()}
            onKeyDown={(e) => handleEnter(e)}
            className="h-11 bg-main w-full"
          >
            <div className="text-sm">{t('Login')}</div>
          </Button>
          <Button
            onClick={() => setOpenResetPwd(true)}
            className="h-11 bg-background-100-70 w-full"
          >
            <div className="text-sm text-text-400">{t('Find my password')}</div>
          </Button>
        </div>
        <div className="space-y-0">
          <div className="flex items-center justify-between">
            <div className="text-muted-foreground text-xs">{t("Don't have an account?")}</div>
            <Button variant="ghost" onClick={onRegister}>
              <div className="text-primary-500 text-sm">{t('Sign Up')}</div>
            </Button>
          </div>
          <div className="cursor-pointer" onClick={() => setOpenReverify(true)}>
            <div className="text-muted-foreground text-xs">{t('Resend Email Verification')}</div>
          </div>
        </div>
      </div>

      <FindPassword open={openResetPwd} handleClose={() => setOpenResetPwd(false)} />
      <Reverify open={openReverify} handleClose={() => setOpenReverify(false)} />
    </>
  );
};

export default SigninContent;
